import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DocumentPriceEnum, DOCUMENT_PRICES } from 'src/app/shared/util/document-prices.enum';
import { StripePaymentService } from 'src/app/shared/services/stripe-payment.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from 'src/app/shared/auth/auth.service';
import { CardPayment } from 'src/app/shared/services/stripe-payment.service';
import { Utilities } from 'src/app/shared/util/utilities';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-card-payment',
  templateUrl: './card-payment.component.html',
  styleUrls: ['./card-payment.component.scss']
})
export class CardPaymentComponent implements OnInit {
  documentFormat: string = 'pdf';
  documentUniqueName: string = '';
  documentUniqueGroup: string = '';
  documentType: string = '';
  documentPrice: number = 0.00;
  documentCurrency: string = 'RON';

  isLoading: boolean = false;
  showButtons: boolean = false;

  existingRequest: CardPayment | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private stripePaymentService: StripePaymentService,
    private snackBar: MatSnackBar,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.isLoading = true;
    this.route.queryParams.subscribe(params => {
      this.documentType = params['documentType'];
      this.documentUniqueName = params['documentUniqueName'];
      this.documentFormat = params['documentFormat'];
      this.documentUniqueGroup = params['documentUniqueGroup'];
      this.documentPrice = params['documentPrice'];
      this.documentCurrency = params['documentCurrency'];

      if (!this.documentUniqueName) {
        this.isLoading = false;
        this.showButtons = true;

        this.snackBar.open('Nu s-a specificat un document. Te rugăm să încerci din nou.', 'Închide', {
          duration: 5000
        });

        return;
      }

      this.stripePaymentService.getDocumentDetails(this.documentUniqueName).subscribe({
        next: (response) => {
          if (!response || !response.documentDetails) {
            console.error('Received empty document details');
            this.handleDocumentDetailsError('No document details received');
            return;
          }

          this.documentType = response.documentDetails.documentType || 'Unknown Document';
          this.documentUniqueGroup = response.documentDetails.documentUniqueGroup || 'unknown_document_pdf';
          this.documentUniqueName = response.documentDetails.documentUniqueName || 'unknown_document';
          this.documentFormat = (response.documentDetails.documentFormat || 'pdf').toLowerCase();
          this.documentPrice = parseFloat(response.documentDetails.documentPrice);
          this.documentCurrency = response.documentDetails.documentCurrency || 'RON';

          this.existingRequest = response.documentRequests.length == 0 ? null : response.documentRequests[0];

          this.isLoading = false;
          this.showButtons = true;
        },
        error: (error) => {
          console.error('Error fetching document details:', error);
          this.handleDocumentDetailsError('Eroare la procesarea detaliilor documentului');
        }
      });
    });
  }

  async initiatePayment() {
    this.isLoading = true;

    if (!this.documentUniqueName) {
      console.error('Missing required document information for payment', {
        documentUniqueName: this.documentUniqueName,
      });

      this.snackBar.open('Informații insuficiente despre document. Vă rugăm reîncercați.', 'Închide', {
        duration: 5000
      });

      this.isLoading = false;
      return;
    }

    try {
      const user = this.authService.getCurrentUserSync();

      if (this.documentPrice === 0) {
        const timestamp = new Date().getTime();
        const stripeSessionId = `free_${this.documentUniqueName}_${timestamp}`;
        this.router.navigate(['/card-payment-success', stripeSessionId]);
        return;
      }

      if (this.existingRequest) {
        this.router.navigate(['/card-payment-success', this.existingRequest.stripeSessionId]);
        return;
      }

      this.createStripeCheckoutSession().subscribe({
        next: (response) => {
          if (response && response.sessionId) {
            console.log('Redirecting to Stripe checkout:', response.sessionId);
            // Redirect to Stripe checkout
            this.stripePaymentService.redirectToCheckout(response.sessionId)
              .catch(error => {
                console.error('Redirect error:', error);
                this.isLoading = false;
                this.snackBar.open('Failed to redirect to checkout page. Please try again.', 'Close', {
                  duration: 5000,
                });
              });
          } else {
            console.error('Invalid session response:', response);
            this.snackBar.open('Failed to create checkout session. Please try again.', 'Close', {
              duration: 5000,
            });
            this.isLoading = false;
          }
        },
        error: (error) => {
          console.error('Create checkout session error:', error);
          this.snackBar.open('Failed to create checkout session. Please try again.', 'Close', {
            duration: 5000,
          });
          this.isLoading = false;
        }
      });
    } catch (error) {
      console.error('Error in initiatePayment:', error);
      this.isLoading = false;
      this.snackBar.open('Eroare la inițierea plății. Vă rugăm reîncercați.', 'Închide', {
        duration: 5000
      });
    }
  }

  private handleDocumentDetailsError(message: string) {
    this.documentPrice = 0.00;
    this.documentCurrency = 'RON';
    this.documentUniqueGroup = 'unknown_document_pdf';
    this.documentUniqueName = 'unknown_document';
    this.documentType = 'Unknown Document';
    this.documentFormat = 'pdf';

    this.existingRequest = null;

    this.isLoading = false;
    this.showButtons = true;

    this.snackBar.open(`${message}. Se folosesc prețuri implicite.`, 'Închide', {
      duration: 5000
    });
  }

  private createStripeCheckoutSession(): Observable<{ sessionId: string, paymentId: number, userId?: number }> {
    const user = this.authService.getCurrentUserSync();
    const idUser = user ? parseInt(user.id) : null;
    const email = user ? user.email : '';

    return this.stripePaymentService.createCheckoutSession(
      this.documentUniqueName,
      idUser,
      email,
    );
  }

  goBack() {
    this.router.navigate(['/documents']);
  }
} 